import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { useMutation, useLazyQuery } from "@apollo/client";
import { LOGIN_MUTATION } from "api/graphql/queries/loginQuery";
import { SELLER_LOGOUT } from "api/graphql/queries/sellerLogoutQuery";
import { GET_SELLER_PROFILE } from "api/graphql/queries/getSellerProfileQuery";
// Seller interface remains the same
interface Seller {
  entity_id: string;
  name: string;
  email: string;
  is_seller: string;
  shop_url: string;
  shop_title: string;
  logo_pic: string;
  company_locality: string;
  contact_number: string;
  billing_full: string;
  billing_firstname: string;
  billing_lastname: string;
  billing_telephone: string;
  billing_postcode: string;
  billing_country_id: string;
  billing_region: string;
  billing_region_id: string;
  billing_street: string;
  billing_city: string;
  billing_company: string;
  [key: string]: any;
}

interface AuthContextProps {
  isAuthenticated: boolean | null;
  seller: Seller | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setSeller: React.Dispatch<React.SetStateAction<Seller | null>>;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean | null>>;
  profileLoading: boolean;
  loginLoading: boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [seller, setSeller] = useState<Seller | null>(null);

  // Login mutation
  const [sellerLogin, { data: loginData, loading: loginLoading, error: loginError }] = useMutation(LOGIN_MUTATION);


  // Get profile query
  const [getProfile, { loading: profileLoading }] = useLazyQuery(GET_SELLER_PROFILE, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.getSellerProfile?.success) {
        const storedSeller = localStorage.getItem("seller");
        if (storedSeller) {
          setSeller(JSON.parse(storedSeller));
        }
        setIsAuthenticated(true);
      } else {
        // Token is invalid or expired
        localStorage.removeItem("token");
        localStorage.removeItem("seller");
        localStorage.removeItem("sid");
        setSeller(null);
        setIsAuthenticated(false);
      }
    },
    onError: () => {
      // Error occurred, token is likely invalid
      localStorage.removeItem("token");
      localStorage.removeItem("seller");
      localStorage.removeItem("sid");
      setSeller(null);
      setIsAuthenticated(false);
    }
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    
    if (token) {
      // Set isAuthenticated to null during loading
      setIsAuthenticated(null);
      // Validate the token by getting the profile
      getProfile();
    } else {
      setIsAuthenticated(false);
      setSeller(null);
    }
  }, [getProfile]);

  // Login function
  const login = async (email: string, password: string) => {
    try {
      console.log("AuthProvider: Starting login process");
      
      // First check if token already exists and clear it to avoid state inconsistencies
      if (localStorage.getItem("token")) {
        localStorage.removeItem("token");
        localStorage.removeItem("seller");
        localStorage.removeItem("sid");
      }
      
      const response = await sellerLogin({
        variables: {
          username: email,
          password,
        },
      });
      
      console.log("AuthProvider: Received login response", response);
      
      if (response?.data?.sellerLogin?.success) {
        // Save token immediately for next API calls
        localStorage.setItem("token", response.data.sellerLogin.token);
        
        // IMPORTANT: Don't save seller data from login response yet, as it might be outdated
        // Instead, immediately fetch fresh profile data
        try {
          console.log("AuthProvider: Fetching latest profile data after login");
          const profileResult = await getProfile();
          
          if (profileResult?.data?.getSellerProfile?.success) {
            // Now we have the latest profile data
            const freshSellerData = {
              ...response.data.sellerLogin.seller, // Base seller data
              ...profileResult.data.getSellerProfile.data // Override with fresh data
            };
            
            // Save the combined fresh data to localStorage
            localStorage.setItem("seller", JSON.stringify(freshSellerData));
            
            // Update state with the fresh data
            setSeller(freshSellerData);
            setIsAuthenticated(true);
          } else {
            // Fallback to login data if profile fetch fails
            localStorage.setItem("seller", JSON.stringify(response.data.sellerLogin.seller));
            setSeller(response.data.sellerLogin.seller);
            setIsAuthenticated(true);
          }
        } catch (profileError) {
          console.error("AuthProvider: Error fetching profile after login", profileError);
          // Fallback to login data if profile fetch fails
          localStorage.setItem("seller", JSON.stringify(response.data.sellerLogin.seller));
          setSeller(response.data.sellerLogin.seller);
          setIsAuthenticated(true);
        }
        
        return;
      } else {
        // Handle unsuccessful login with specific message
        const errorMessage = response?.data?.sellerLogin?.message || "Login failed: Invalid credentials";
        console.error("AuthProvider: Login unsuccessful", errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error("AuthProvider: Login error caught:", error);
      
      // Ensure we're in a clean state
      localStorage.removeItem("token");
      localStorage.removeItem("seller");
      localStorage.removeItem("sid");
      
      // Extract GraphQL error message if available
      const graphQLErrors = error?.graphQLErrors;
      let errorMessage;
      
      if (graphQLErrors && graphQLErrors.length > 0) {
        errorMessage = graphQLErrors[0].message;
      } else if (error.networkError) {
        errorMessage = "Network error: Please check your connection and try again";
      } else {
        errorMessage = error.message || "An unexpected error occurred during login";
      }
      
      console.error("AuthProvider: Throwing error with message:", errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Logout function - frontend only implementation
  const logout = async () => {
    try {
      // Remove data from localStorage
      localStorage.removeItem("token");
      localStorage.removeItem("seller");

      // Clear state
      setSeller(null);
      setIsAuthenticated(false);

        localStorage.removeItem("token");
        localStorage.removeItem("seller");
        localStorage.removeItem("sid");
        setSeller(null);
        setIsAuthenticated(false);

        // window.location.href = "/auth/login";
      
    } catch (error) {
      console.error("Logout failed:", error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        seller,
        login,
        logout,
        setSeller,
        setIsAuthenticated,
        profileLoading,
        loginLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
